/**
 * Maps MediKaLLink host sessionStorage keys to API credentials for the VoicNav backend
 * (which forwards them to the MediKaLLink origin from the VoiceNav catalog).
 *
 * Session keys (host app):
 * - reference_key → Bearer token (PI_BEARER_TOKEN)
 * - owner_of_logged_mc_uuid → LOGGED_USER_ID
 * - logged_user_own_med_center_id → MED_CENTER_ID
 * - logged_user_fb_uid / fb_uid (optional) → Firebase id for APIs / active chart bind
 * - logged_user_name → display name for patient portal active chart bind
 */

const SESSION_KEY_REFERENCE = 'reference_key'
const SESSION_KEY_USER_UUID = 'logged_user_uuid'
const SESSION_KEY_USER_NAME = 'logged_user_name'
const SESSION_KEY_USER_TYPE = 'logged_user_type'
const SESSION_KEY_MED_CENTER = 'logged_user_own_med_center_id'
/** Host may store the MediKaLLink Firebase user id for messaging APIs. */
const SESSION_KEY_SENDER_FB_UID_PRIMARY = 'logged_user_fb_uid'
const SESSION_KEY_SENDER_FB_UID_FALLBACK = 'fb_uid'

/** Defaults when sessionStorage is missing or empty (per integration spec). */
export const DEFAULT_PI_BEARER_TOKEN =
  '25554|TwDIeEfu81NZV5U2VrgZAkNbRfZSuvZdmnncM1ma68b28188'
export const DEFAULT_LOGGED_USER_ID = '17e56335-aa98-4f64-8ca8-2334755c50f7'
export const DEFAULT_MED_CENTER_ID = '1002'

export type MedicalLinkCredentialSource = 'session' | 'default'

export type MedicalLinkCredentials = {
  piBearerToken: string
  loggedUserId: string
  medCenterId: string
  /** MediKaLLink portal user type (e.g. doctor = 4, patient = 5). */
  loggedUserType?: string
  /** Logged-in user display name (Laravel sessionStorage ``logged_user_name``). */
  loggedUserName?: string
  /** Present when session or widget config supplies Firebase uid for discussion APIs. */
  senderFbUid?: string
}

export type MedicalLinkCredentialResolution = MedicalLinkCredentials & {
  sources: {
    piBearerToken: MedicalLinkCredentialSource
    loggedUserId: MedicalLinkCredentialSource
    loggedUserType: MedicalLinkCredentialSource
    loggedUserName: MedicalLinkCredentialSource
    medCenterId: MedicalLinkCredentialSource
    senderFbUid: MedicalLinkCredentialSource
  }
}

function readTrimmed(key: string): string | undefined {
  if (typeof sessionStorage === 'undefined') return undefined
  try {
    const v = sessionStorage.getItem(key)
    const t = v?.trim()
    return t || undefined
  } catch {
    return undefined
  }
}

/**
 * Read current credentials (call before each API/WebSocket start so late login is picked up).
 */
export function getMedicalLinkCredentialsFromSession(): MedicalLinkCredentialResolution {
  const ref = readTrimmed(SESSION_KEY_REFERENCE)
  const user = readTrimmed(SESSION_KEY_USER_UUID)
  const userType = readTrimmed(SESSION_KEY_USER_TYPE)
  const userName = readTrimmed(SESSION_KEY_USER_NAME)
  const center = readTrimmed(SESSION_KEY_MED_CENTER)
  const senderFbPrimary = readTrimmed(SESSION_KEY_SENDER_FB_UID_PRIMARY)
  const senderFbFallback = readTrimmed(SESSION_KEY_SENDER_FB_UID_FALLBACK)
  const senderFb = senderFbPrimary ?? senderFbFallback

  return {
    piBearerToken: ref ?? DEFAULT_PI_BEARER_TOKEN,
    loggedUserId: user ?? DEFAULT_LOGGED_USER_ID,
    loggedUserType: userType,
    loggedUserName: userName,
    medCenterId: center ?? DEFAULT_MED_CENTER_ID,
    senderFbUid: senderFb,
    sources: {
      piBearerToken: ref ? 'session' : 'default',
      loggedUserId: user ? 'session' : 'default',
      loggedUserType: userType ? 'session' : 'default',
      loggedUserName: userName ? 'session' : 'default',
      medCenterId: center ? 'session' : 'default',
      senderFbUid: senderFb ? 'session' : 'default',
    },
  }
}

/** JSON field names expected by the VoicNav backend. */
export function medicalLinkCredentialsToApiPayload(c: MedicalLinkCredentials): {
  pi_bearer_token: string
  logged_user_id: string
  med_center_id: string
  logged_user_type?: string
  logged_user_name?: string
  sender_fb_uid?: string
} {
  const out: {
    pi_bearer_token: string
    logged_user_id: string
    med_center_id: string
    logged_user_type?: string
    logged_user_name?: string
    sender_fb_uid?: string
  } = {
    pi_bearer_token: c.piBearerToken,
    logged_user_id: c.loggedUserId,
    med_center_id: c.medCenterId,
  }
  const userType = (c.loggedUserType ?? '').trim()
  if (userType) {
    out.logged_user_type = userType
  }
  const userName = (c.loggedUserName ?? '').trim()
  if (userName) {
    out.logged_user_name = userName
  }
  const fb = (c.senderFbUid ?? '').trim()
  if (fb) {
    out.sender_fb_uid = fb
  }
  return out
}

let didLogCredentialSource = false

/**
 * Log once whether each value came from sessionStorage or defaults.
 */
export function logMedicalLinkCredentialSourceOnce(): void {
  if (didLogCredentialSource) return
  didLogCredentialSource = true
  if (typeof console === 'undefined' || typeof sessionStorage === 'undefined') return

  const { sources } = getMedicalLinkCredentialsFromSession()
  const senderFbKey =
    ((): string => {
      try {
        const vFallback = sessionStorage.getItem(SESSION_KEY_SENDER_FB_UID_FALLBACK)?.trim()
        if (vFallback) return SESSION_KEY_SENDER_FB_UID_FALLBACK
        const vPrimary = sessionStorage.getItem(SESSION_KEY_SENDER_FB_UID_PRIMARY)?.trim()
        if (vPrimary) return SESSION_KEY_SENDER_FB_UID_PRIMARY
      } catch {
        // ignore
      }
      return SESSION_KEY_SENDER_FB_UID_FALLBACK
    })()
  const parts = [
    `PI_BEARER_TOKEN (reference_key): ${sources.piBearerToken === 'session' ? 'sessionStorage' : 'default fallback'}`,
    `LOGGED_USER_ID (logged_user_uuid): ${sources.loggedUserId === 'session' ? 'sessionStorage' : 'default fallback'}`,
    `LOGGED_USER_NAME (logged_user_name): ${sources.loggedUserName === 'session' ? 'sessionStorage' : 'default fallback (missing)'}`,
    `MED_CENTER_ID (logged_user_own_med_center_id): ${sources.medCenterId === 'session' ? 'sessionStorage' : 'default fallback'}`,
    `SENDER_FB_UID (${senderFbKey}): ${sources.senderFbUid === 'session' ? 'sessionStorage' : 'default fallback (missing)'}`,
  ]
  console.info(`[VoicNav] Medical Link credentials — ${parts.join('; ')}`)
}
